import React, { useState, useEffect } from 'react';
import Dashboard from './components/Dashboard';
import { ConditionManagement } from './components/ConditionManagement';
import { SymptomTracking } from './components/SymptomTracking';
import { AdvancedSymptomTracker } from './components/symptoms/AdvancedSymptomTracker';
import { DocumentAnalysis } from './components/ai/DocumentAnalysis';
import { DeviceIntegration } from './components/telemetry/DeviceIntegration';
import { AdvancedAnalytics } from './components/analytics/AdvancedAnalytics';
import { MedicationManager } from './components/MedicationManager';
import { AppointmentScheduler } from './components/AppointmentScheduler';
import { DataExport } from './components/DataExport';
import { FoodTracking } from './components/food/FoodTracking';
import { ProviderDashboard } from './components/providers/ProviderDashboard';
import { HolisticTreatmentTracker } from './components/holistic/HolisticTreatmentTracker';
import { LabTechDashboard } from './components/lab/LabTechDashboard';
import { LabResultAnalyzer } from './components/lab/LabResultAnalyzer';
import { EnhancedFoodTracker } from './components/food/EnhancedFoodTracker';
import { MealLogDashboard } from './components/food/MealLogDashboard';
import { Header } from './components/Header';
import { SecurityDashboard } from './components/security/SecurityDashboard';
import { Settings } from './components/Settings';
import { LandingPage } from './components/LandingPage';
import LoginPage from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import { UserManagement } from './admin/UserManagement';
import { useAuth } from './providers/AuthProvider';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { UserEncryptionManager } from './components/encryption/UserEncryptionManager'; 
import { P2PDocumentManager } from './components/blockchain/P2PDocumentManager'; 
import { InnovativeFeatures } from './components/innovative/InnovativeFeatures'; 
import { UserProfile } from './components/UserProfile';
import { AIHealthCoach } from './components/ai/AIHealthCoach'; 
import { GuruConnect } from './components/guru/GuruConnect';
import { GuruDashboard } from './components/guru/GuruDashboard';
import { HealthMarketplace } from './components/guru/HealthMarketplace';
import { TrustScoreSystem } from './components/guru/TrustScoreSystem';
import { ComingSoonBanner } from './components/common/ComingSoonBanner';
import { UserTutorial } from './components/common/UserTutorial';
import EnhancedOnboarding from './components/common/EnhancedOnboarding';
import { UserPersonalizationProfile } from './types/users';
import LayoutOptions from './layout/LayoutOptions';
import DesignPreview from './pages/DesignPreview';
import MealPlanner from './components/meal-planning/MealPlanner';
import RecipeBook from './components/recipes/RecipeBook';
import GroceryListPlanner from './components/grocery/GroceryListPlanner';
import AdvancedMealRecommendations from './components/food/AdvancedMealRecommendations';
import About from './pages/About';
import Pricing from './pages/Pricing';
import Changelog from './pages/Changelog';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Contact from './pages/Contact';
import ErrorBoundary from './components/ErrorBoundary';
import versionManager from './utils/versionManager';
import './styles/design-preview.css';
import TermsOfService from './legal/TermsOfService';
import { UserFeedbackWidget } from './components/feedback/UserFeedbackWidget';
import { Navigation } from './shared/ui/Navigation';
import { ResponsiveLayout } from './components/layout/ResponsiveLayout';
import { ContentCreation } from './components/guru/ContentCreation';
import { LiveSessions } from './components/guru/LiveSessions';
import { PredictiveInsights } from './components/guru/PredictiveInsights';
import { PWAFeatures } from './components/guru/PWAFeatures';
import { DataSharing } from './components/guru/DataSharing';
import FeedbackSystem from './components/common/FeedbackSystem';
import { useFeedback } from './hooks/useFeedback';
import { errorLogger } from './utils/errorLogger';
import { autoErrorFixer } from './utils/autoErrorFixer';
import { navigationManager, NavigationTarget } from './utils/navigationManager';
import SystemStatusPage from './components/SystemStatusPage';
import OnboardingPrompt from './components/common/OnboardingPrompt';
import { useOnboardingGuidance } from './hooks/useMockDataDetection';

export type ActiveView = 'dashboard' | 'conditions' | 'symptoms' | 'advanced-symptoms' | 'ai-docs' | 'ai-coach' | 'devices' | 'analytics' | 'medications' | 'appointments' | 'export' | 'food-tracking' | 'enhanced-food-tracker' | 'meal-log' | 'meal-planner' | 'recipe-book' | 'grocery-list' | 'meal-recommendations' | 'holistic-treatments' | 'provider-dashboard' | 'lab-tech' | 'lab-analyzer' | 'settings' | 'security' | 'admin' | 'encryption' | 'p2p-docs' | 'innovations' | 'guru-connect' | 'guru-dashboard' | 'health-marketplace' | 'trust-score' | 'content-creation' | 'live-sessions' | 'predictive-insights' | 'pwa-features' | 'data-sharing' | 'design-preview' | 'layout-options' | 'about' | 'pricing' | 'changelog' | 'privacy-policy' | 'contact' | 'user-management' | 'legal-terms' | 'system-status';
export type AppState = 'landing' | 'login' | 'reset-password' | 'app';

function AppNew() {
  const { user, signOut, loading, updateProfile, updateProfileDetails, error: authError, isConfigured } = useAuth();
  const [activeView, setActiveView] = useState<ActiveView>('dashboard');
  const [appState, setAppState] = useState<AppState>('landing');
  const [showProfile, setShowProfile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showEnhancedOnboarding, setShowEnhancedOnboarding] = useState(false);
  const [userPersonalizationProfile, setUserPersonalizationProfile] = useState<UserPersonalizationProfile | null>(null);
  const [darkMode, setDarkMode] = useState(() => {
    // Check for user preference in localStorage or system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const feedback = useFeedback();
  const { showOnboardingPrompt, dismissOnboardingPrompt, startOnboarding } = useOnboardingGuidance();

  // Initialize error handling system and navigation manager
  useEffect(() => {
    // Configure navigation manager
    navigationManager.updateConfig({
      onViewChange: (view: NavigationTarget) => {
        setActiveView(view.id as ActiveView);
      },
      onError: (error: Error) => {
        errorLogger.error(
          'Navigation error',
          {
            component: 'NavigationManager',
            error,
            context: { currentView: activeView }
          }
        );
      },
      onSuccess: () => {
        console.log('Navigation successful');
      }
    });
    
    // Set up error monitoring for AI Insights specifically
    const handleAIError = (event: CustomEvent) => {
      const error = event.detail?.error;
      if (error) {
        errorLogger.error(
          'AI Insights error',
          {
            component: 'AIInsights',
            error,
            context: {
              action: 'ai_insights_navigation',
              currentPath: window.location.pathname,
              targetPath: '/ai-coach'
            }
          }
        );

        // Attempt auto-fix
        autoErrorFixer.attemptAutoFix(error, {
          operation: 'ai_navigation',
          currentPath: window.location.pathname,
          targetPath: '/ai-coach'
        }).then(result => {
          if (result.success) {
            console.log('Auto-fix successful:', result.message);
          } else {
            console.warn('Auto-fix failed:', result.message);
          }
        });
      }
    };

    window.addEventListener('ai-insights-error', handleAIError as EventListener);
    
    return () => {
      window.removeEventListener('ai-insights-error', handleAIError as EventListener);
    };
  }, [activeView]);

  // Check URL for reset password route
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    
    // Check for password reset URLs
    if (path === '/reset-password' || 
        urlParams.get('type') === 'recovery' || 
        urlParams.has('access_token') && urlParams.has('refresh_token')) {
      setAppState('reset-password');
    }
  }, []);

  // Handle direct navigation to legal/terms and privacy-policy
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/legal/terms') {
      setActiveView('legal-terms');
      setAppState('landing');
    } else if (path === '/privacy-policy') {
      setActiveView('privacy-policy');
      setAppState('landing');
    }
  }, []);

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Save preference to localStorage
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // Initialize version management (only in production to avoid dev reload issues)
  useEffect(() => {
    // Skip version management in development
    return;

    // Check for version mismatch on app load
    const versionMismatch = versionManager.checkVersion();
    
    if (versionMismatch) {
      console.log('Version mismatch detected, handling...');
      versionManager.handleVersionMismatch();
      return; // Don't continue with normal initialization
    }

    // Update version info for this session
    versionManager.updateStoredVersion();
    
    // Start periodic update checks
    versionManager.startPeriodicCheck();
  }, []);

  // Handle reset password state BEFORE user authentication checks
  if (appState === 'reset-password') {
    return (
      <ResetPassword
        onSuccess={() => {
          // Clear URL parameters and redirect to login
          window.history.replaceState({}, document.title, window.location.pathname);
          setAppState('login');
        }}
        onBack={() => {
          // Clear URL parameters and redirect to login
          window.history.replaceState({}, document.title, window.location.pathname);
          setAppState('login');
        }}
      />
    );
  }

  // Handle app state transitions - moved before conditional returns
  useEffect(() => {
    if (!user) return

    // Check if user has demo data and show onboarding
    const hasExistingData = localStorage.getItem('demo_conditions') ||
                           localStorage.getItem('demo_symptoms') ||
                           localStorage.getItem('demo_medications');
    
    const hasSeenOnboarding = localStorage.getItem('zippyhealth_onboarding_complete');
    
    // Auto-transition to app state after a short delay for demo purposes
    const timer = setTimeout(() => {
      if (user) {
        setAppState('app');

        // Decide initial view based on user state
        if (!hasSeenOnboarding) {
          setActiveView('dashboard'); // Start with dashboard for new users
          // Show enhanced onboarding for new users
          setTimeout(() => {
            const savedProfile = localStorage.getItem('zippyhealth_personalization_profile');
            if (savedProfile) {
              setUserPersonalizationProfile(JSON.parse(savedProfile));
            } else {
              // Show enhanced onboarding for new users
              setShowEnhancedOnboarding(true);
            }
          }, 500);
        } else if (hasExistingData) {
          setActiveView('dashboard'); // Returning users see their data
        } else {
          setActiveView('meal-log'); // New feature for empty accounts
        }
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [user]);

  // Handle loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading ZippyHealth</h2>
          <p className="text-gray-600">Setting up your secure health environment...</p>
        </div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-red-200 p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Error</h2>
            <p className="text-gray-600 mb-4">{authError}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle unconfigured state
  if (!isConfigured) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-red-200 p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Configuration Required</h2>
            <p className="text-gray-600 mb-4">Please check your environment configuration and try again.</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle landing state
  if (appState === 'landing') {
    return (
      <LandingPage 
        onGetStarted={() => setAppState('login')}
        onNavigate={(section) => {
          switch (section) {
            case 'about':
              setActiveView('about');
              break;
            case 'pricing':
              setActiveView('pricing');
              break;
            case 'contact':
              setActiveView('contact');
              break;
            case 'privacy-policy':
              setActiveView('privacy-policy');
              break;
            case 'legal-terms':
              setActiveView('legal-terms');
              break;
            case 'changelog':
              setActiveView('changelog');
              break;
            case 'design-preview':
              setActiveView('design-preview');
              break;
            case 'layout-options':
              setActiveView('layout-options');
              break;
            default:
              // Handle other navigation cases
              break;
          }
        }}
      />
    );
  }

  // Handle login state
  if (appState === 'login') {
    return (
      <LoginPage 
        onSuccess={() => setAppState('app')}
        onBack={() => setAppState('landing')}
      />
    );
  }

  // Handle app state with user
  if (appState === 'app' && user) {
    const handleBackToApp = () => {
      setAppState('app');
    };

    const handleBackToLanding = () => {
      setAppState('landing');
    };

    const handleLogout = async () => {
      try {
        await signOut();
        setAppState('landing');
        setActiveView('dashboard');
      } catch (error) {
        console.error('Logout error:', error);
        // Force logout even if there's an error
        setAppState('landing');
        setActiveView('dashboard');
      }
    };

    const handleViewChange = (view: ActiveView) => {
      setActiveView(view);
    };

    const handleNavigationWrapper = (view: string) => {
      // Handle navigation with error catching
      try {
        setActiveView(view as ActiveView);
      } catch (error) {
        errorLogger.error(
          'Navigation error',
          {
            component: 'Navigation',
            error,
            context: { targetView: view, currentView: activeView }
          }
        );
      }
    };

    const mapUserRole = (role: string): 'guru' | 'client' | undefined => {
      switch (role) {
        case 'healthcare_provider':
        case 'lab_technician':
        case 'super_admin':
          return 'guru';
        case 'patient':
        case 'demo':
          return 'client';
        default:
          return undefined;
      }
    };

    const handleProfileSave = async (updatedData: Partial<any>) => {
      try {
        await updateProfile(updatedData);
        setShowProfile(false);
      } catch (error) {
        console.error('Profile update error:', error);
        errorLogger.error(
          'Profile update error',
          {
            component: 'ProfileUpdate',
            error,
            context: { updatedData }
          }
        );
      }
    };

    const handleStartOnboarding = () => {
      startOnboarding();
      setShowEnhancedOnboarding(true);
    };

    const renderActiveView = () => {
      try {
        switch (activeView) {
          case 'dashboard':
            return <Dashboard />;
          case 'conditions':
            return <ConditionManagement />;
          case 'symptoms':
            return <SymptomTracking />;
          case 'advanced-symptoms':
            return <AdvancedSymptomTracker />;
          case 'ai-docs':
            return <DocumentAnalysis />;
          case 'ai-coach':
            return <AIHealthCoach />;
          case 'devices':
            return <DeviceIntegration />;
          case 'analytics':
            return <AdvancedAnalytics />;
          case 'medications':
            return <MedicationManager />;
          case 'appointments':
            return <AppointmentScheduler />;
          case 'export':
            return <DataExport />;
          case 'food-tracking':
            return <FoodTracking />;
          case 'enhanced-food-tracker':
            return <EnhancedFoodTracker />;
          case 'meal-log':
            return <MealLogDashboard />;
          case 'meal-recommendations':
            return userPersonalizationProfile ? (
              <AdvancedMealRecommendations 
                profile={userPersonalizationProfile} 
                onSelectMeal={(mealName) => {
                  // Could integrate with meal logger here
                  console.log('Selected meal:', mealName);
                }}
              />
            ) : (
              <div className="p-6 text-center">
                <p className="text-slate-600">Complete onboarding to see personalized recommendations</p>
              </div>
            );
          case 'meal-planner':
            return <MealPlanner />;
          case 'recipe-book':
            return <RecipeBook />;
          case 'grocery-list':
            return <GroceryListPlanner />;
          case 'holistic-treatments':
            return <HolisticTreatmentTracker />;
          case 'provider-dashboard':
            return user?.role === 'healthcare_provider' ? <ProviderDashboard /> : <Dashboard />;
          case 'lab-tech':
            return <LabTechDashboard />;
          case 'lab-analyzer':
            return <LabResultAnalyzer />;
          case 'security':
            return <SecurityDashboard />;
          case 'settings':
            return <Settings onNavigate={handleNavigationWrapper} />;
          case 'admin':
            return user.role === 'super_admin' ? <AdminDashboard /> : <Dashboard />;
          case 'encryption':
            return <UserEncryptionManager />;
          case 'p2p-docs':
            return <P2PDocumentManager />;
          case 'innovations':
            return <InnovativeFeatures />;
          case 'guru-connect':
            return <GuruConnect />;
          case 'guru-dashboard':
            return <GuruDashboard />;
          case 'health-marketplace':
            return <HealthMarketplace />;
          case 'trust-score':
            return <TrustScoreSystem />;
          case 'content-creation':
            return <ContentCreation />;
          case 'live-sessions':
            return <LiveSessions />;
          case 'predictive-insights':
            return <PredictiveInsights />;
          case 'pwa-features':
            return <PWAFeatures userRole={mapUserRole(user?.role || 'user')} />;
          case 'data-sharing':
            return <DataSharing userRole={mapUserRole(user?.role || 'user')} />;
          case 'layout-options':
            return <LayoutOptions />;
          case 'design-preview':
            return <DesignPreview />;
          case 'about':
            return <About />;
          case 'pricing':
            return <Pricing />;
          case 'changelog':
            return <Changelog />;
          case 'privacy-policy':
            return <PrivacyPolicy />;
          case 'contact':
            return <Contact />;
          case 'user-management':
            return <UserManagement onBack={() => handleViewChange('settings')} />;
          case 'legal-terms':
            return <TermsOfService />;
          case 'system-status':
            return <SystemStatusPage />;
          default:
            return <Dashboard />;
        }
      } catch (error) {
        errorLogger.error(
          'ActiveView error',
          {
            component: 'ActiveView',
            error,
            context: { activeView, userRole: user?.role }
          }
        );
        
        // Return dashboard as fallback
        return <Dashboard />;
      }
    };

    return (
      <ErrorBoundary>
        <ResponsiveLayout
          navigation={
            <Navigation 
              activeView={activeView} 
              onViewChange={handleViewChange}
              userRole={user.role}
              showMobileMenu={showMobileMenu}
              setShowMobileMenu={setShowMobileMenu}
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
            />
          }
          header={
            <Header 
              currentUser={user}
              onLogout={handleLogout}
              onOpenProfile={() => setShowProfile(true)}
              onToggleMobileMenu={() => setShowMobileMenu(!showMobileMenu)}
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
              onNavigate={handleNavigationWrapper}
            />
          }
          showMobileMenu={showMobileMenu}
          setShowMobileMenu={setShowMobileMenu}
        >
          {renderActiveView()}
        </ResponsiveLayout>
        
        {/* User Profile Modal */}
        <UserProfile
          user={user}
          isOpen={showProfile}
          onClose={() => setShowProfile(false)}
          onSave={handleProfileSave}
        />

        {/* User Tutorial */}
        <UserTutorial
          isVisible={showTutorial}
          onComplete={() => setShowTutorial(false)}
        />

        {/* Enhanced Onboarding */}
        <EnhancedOnboarding
          isVisible={showEnhancedOnboarding}
          onComplete={(profile) => {
            const completeProfile: UserPersonalizationProfile = {
              id: `profile-${Date.now()}`,
              userId: user.id,
              preferredLanguage: profile.preferredLanguage || 'en',
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              country: profile.country || 'US',
              region: profile.region,
              healthGoals: profile.healthGoals || [],
              activityLevel: profile.activityLevel || 'moderately_active',
              sleepSchedule: profile.sleepSchedule || { bedtime: '22:00', wakeTime: '07:00' },
              eatingHabits: profile.eatingHabits || {
                mealsPerDay: 3,
                snacking: 'occasionally',
                cookingFrequency: 'sometimes',
                diningOut: 'weekly'
              },
              foodPreferences: profile.foodPreferences || {
                favoriteCategories: [],
                favoriteCuisines: [],
                favoriteSnacks: [],
                dislikes: [],
                allergies: []
              },
              dietaryRestrictions: profile.dietaryRestrictions || [],
              aiPreferences: profile.aiPreferences || {
                personalityStyle: 'encouraging',
                responseLength: 'brief',
                reminderFrequency: 'medium',
                challengeLevel: 'moderate'
              },
              onboardingStatus: profile.onboardingStatus || {
                completed: true,
                currentStep: 9,
                completedSteps: [],
                skippedSteps: [],
                completedAt: new Date().toISOString()
              },
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
            
            setUserPersonalizationProfile(completeProfile);
            localStorage.setItem('zippyhealth_personalization_profile', JSON.stringify(completeProfile));
            localStorage.setItem('zippyhealth_onboarding_complete', 'true');
            setShowEnhancedOnboarding(false);
          }}
          onSkip={() => {
            setShowEnhancedOnboarding(false);
            localStorage.setItem('zippyhealth_onboarding_complete', 'true');
          }}
        />

        {/* Feedback System */}
        <FeedbackSystem />

        {/* User Feedback Widget */}
        <UserFeedbackWidget />

        {/* Onboarding Prompt */}
        <OnboardingPrompt
          isVisible={showOnboardingPrompt}
          onDismiss={dismissOnboardingPrompt}
          onStart={startOnboarding}
        />

        {/* System Status is handled by state-based navigation */}
      </ErrorBoundary>
    );
  }

  // Fallback loading state
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading...</h2>
        <p className="text-gray-600">Please wait while we set up your experience.</p>
      </div>
    </div>
  );
}

export default AppNew; 